import { REDEMPTIONS_TEXT } from "@/constants/reportsRedemptions";
import type { RedemptionsListParams, RedemptionsListResult } from "@/interfaces/reportsRedemptions";
import { apiClient } from "@/lib/apiClient";
import { downloadBlob } from "@/lib/csv";

/** Params -> GET /reports/redemptions query string object (empty values omitted). */
export function toRedemptionsQuery(
  params?: RedemptionsListParams,
): Record<string, string | number> {
  const query: Record<string, string | number> = {};
  if (params?.search) query.search = params.search;
  if (params?.campaign_id?.length) query.campaign_id = params.campaign_id.join(",");
  if (params?.status?.length) query.status = params.status.join(",");
  if (params?.sentFrom) query.sentFrom = params.sentFrom;
  if (params?.sentTo) query.sentTo = params.sentTo;
  if (params?.redeemedFrom) query.redeemedFrom = params.redeemedFrom;
  if (params?.redeemedTo) query.redeemedTo = params.redeemedTo;
  if (params?.sortBy) query.sortBy = params.sortBy;
  if (params?.sortDir) query.sortDir = params.sortDir;
  if (params?.page && params.page > 1) query.page = params.page;
  if (params?.limit) query.limit = params.limit;
  if (params?.includeFilterOptions) query.includeFilterOptions = "true";
  return query;
}

/** The list query minus paging/filter options, plus `export=csv`: the server returns the full filtered set. */
export function toRedemptionsExportQuery(
  params?: RedemptionsListParams,
): Record<string, string | number> {
  const {
    page: _page,
    limit: _limit,
    includeFilterOptions: _options,
    ...rest
  } = toRedemptionsQuery(params);
  return { ...rest, export: "csv" };
}

/**
 * GET /reports/redemptions -- the Redemptions report's ONE endpoint: paginated, searchable,
 * filterable, sortable recipient rows, plus the campaign filter options when `includeFilterOptions`
 * is set.
 */
export async function fetchRedemptions(
  params?: RedemptionsListParams,
): Promise<RedemptionsListResult> {
  const { data } = await apiClient.get<RedemptionsListResult>("/reports/redemptions", {
    params: toRedemptionsQuery(params),
  });
  return data;
}

/**
 * Fetch the server-built redemptions CSV (same search/filters/sort as the table, every page) and
 * save it. Goes through apiClient so the Clerk JWT is attached. Rejects on HTTP error.
 */
export async function downloadRedemptionsCsv(params?: RedemptionsListParams): Promise<void> {
  const { data } = await apiClient.get<Blob>("/reports/redemptions", {
    params: toRedemptionsExportQuery(params),
    responseType: "blob",
  });
  downloadBlob(REDEMPTIONS_TEXT.csvFilename, data);
}
